// Project-level DSM persistence against the real backend.
//
// Reads come from GET /report-engine/logs/5 (design-package log). Writes still
// go to the gateway REST endpoint (POST to create, PUT to update by id).

import { BASE_URL as GATEWAY_BASE_URL } from "services/endpoints";
import { getFeatureEntityLog } from "services/feature-apis";
import type { BidAssociation, Commitment, DsmScopeItem } from "./types";
import { getDsmProjectToken } from "./dsm-auth";
import { migrateScopeItem } from "./classification";

// GATEWAY_BASE_URL already carries the `/gateway/api` prefix the REST write
// endpoints live under; config's bare BASE_URL does not.
const DSM_DOC_URL = `${GATEWAY_BASE_URL}/v1/dsm/doc`;
/** Same feature type the planner card view uses (`GET .../logs/5`). */
export const DESIGN_PACKAGE_LOG_FEATURE_TYPE = 5;
// Bump when the `items` shape changes; older docs get migrated on read.
export const DSM_SCHEMA_VERSION = 3;

export interface ProjectDoc {
  id: string;
  items: DsmScopeItem[];
}

function authHeaders(): Record<string, string> {
  const token = getDsmProjectToken();
  if (!token) throw new Error("DSM: no project token available yet");
  return {
    "content-type": "application/json",
    Authorization: `Bearer ${token}`
  };
}

type AnyRec = Record<string, unknown>;

function asRecord(value: unknown): AnyRec | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as AnyRec)
    : null;
}

function nestedScopeRows(row: AnyRec): unknown[] {
  const keys = [
    "scope_items",
    "design_scopes",
    "design_scope_items",
    "linkedDesignScopes",
    "linked_design_scopes"
  ];
  const out: unknown[] = [];
  for (const key of keys) {
    if (Array.isArray(row[key])) out.push(...(row[key] as unknown[]));
  }
  return out;
}

function toScopeItem(
  raw: AnyRec,
  projectId: string,
  fallbackDpId?: string
): DsmScopeItem {
  const item = migrateScopeItem({
    ...raw,
    id: raw.id ?? "",
    projectId: (raw.projectId as string) || projectId,
    description: raw.description ?? "",
    status: raw.status ?? "NOT_STARTED",
    responsibility: raw.responsibility ?? "",
    notes: raw.notes ?? raw.internal_notes ?? "",
    createdAt: raw.createdAt ?? raw.created_at ?? "",
    updatedAt:
      raw.updatedAt ?? raw.updated_at ?? raw.createdAt ?? raw.created_at ?? ""
  });

  if (!Array.isArray(raw.commitments) && Array.isArray(raw.design_packages)) {
    item.commitments = (raw.design_packages as AnyRec[]).map((link) => {
      const pkg = asRecord(link.design_package);
      const milestone = asRecord(link.design_package_milestone);
      const lod = asRecord(link.level_of_detail);
      return {
        designPackageId:
          (pkg?.id as string) ||
          (link.design_package_id as string) ||
          fallbackDpId ||
          "",
        milestoneId:
          (milestone?.id as string) ||
          (link.design_package_milestone_id as string) ||
          null,
        lod: 1,
        levelOfDetailValueId: String(
          lod?.id ?? link.level_of_detail_value_id ?? ""
        ),
        notes: (link.notes as string) ?? ""
      } satisfies Commitment;
    });
  }

  if (!Array.isArray(raw.bids) && Array.isArray(raw.bid_packages)) {
    item.bids = (raw.bid_packages as AnyRec[]).map(
      (b): BidAssociation => ({
        bidPackageId: String(b.id ?? b.bid_package_id ?? ""),
        role: (b.role as string) ?? "",
        primary: Boolean(b.is_primary ?? b.primary)
      })
    );
  }

  if (fallbackDpId && (!item.commitments || item.commitments.length === 0)) {
    item.commitments = [
      {
        designPackageId: fallbackDpId,
        milestoneId:
          (raw.milestone_id as string) || (raw.milestoneId as string) || null,
        lod: 1,
        levelOfDetailValueId: "",
        notes: ""
      }
    ];
  }

  item.commitments = item.commitments ?? [];
  item.bids = item.bids ?? [];
  return item;
}

function matrixRowsFromRoot(root: AnyRec): {
  matrixId: string;
  rawItems: { row: unknown; fallbackDpId?: string }[];
} {
  let matrixId = "";
  let rawItems: { row: unknown; fallbackDpId?: string }[] = [];
  const matrix =
    asRecord(root.scope_matrix) ?? asRecord(root.feature_design_scope_matrix);
  if (matrix) {
    matrixId = String(matrix.id ?? "");
    const envelope = asRecord(matrix.data) ?? matrix;
    if (Array.isArray(envelope.items)) {
      rawItems = (envelope.items as unknown[]).map((row) => ({ row }));
    }
  }
  if (!rawItems.length && Array.isArray(root.scope_items)) {
    rawItems = (root.scope_items as unknown[]).map((row) => ({ row }));
  }
  return { matrixId, rawItems };
}

function rowsNestedOnPackages(
  packages: unknown[]
): { row: unknown; fallbackDpId?: string }[] {
  const rawItems: { row: unknown; fallbackDpId?: string }[] = [];
  for (const pkg of packages) {
    const rec = asRecord(pkg);
    if (!rec) continue;
    const dpId = typeof rec.id === "string" ? rec.id : undefined;
    for (const row of nestedScopeRows(rec)) {
      rawItems.push({ row, fallbackDpId: dpId });
    }
  }
  return rawItems;
}

function uniqueScopeItems(
  rawItems: { row: unknown; fallbackDpId?: string }[],
  projectId: string
): DsmScopeItem[] {
  const items: DsmScopeItem[] = [];
  const seen = new Set<string>();
  for (const { row, fallbackDpId } of rawItems) {
    const rec = asRecord(row);
    if (!rec) continue;
    const item = toScopeItem(rec, projectId, fallbackDpId);
    if (item.id && seen.has(item.id)) continue;
    if (item.id) seen.add(item.id);
    items.push(item);
  }
  return items;
}

/**
 * Pull DSM items out of a GET /report-engine/logs/5 payload. Accepts a bare
 * design-package array, a `{ scope_matrix, design_packages }` wrapper, or
 * scope rows nested on each package.
 */
export function projectDocFromDesignPackageLog(
  data: unknown,
  projectId: string
): ProjectDoc {
  let matrixId = "";
  let rawItems: { row: unknown; fallbackDpId?: string }[] = [];

  const root = asRecord(data);
  if (root && !Array.isArray(data)) {
    const fromRoot = matrixRowsFromRoot(root);
    matrixId = fromRoot.matrixId;
    rawItems = fromRoot.rawItems;
    data = root.design_packages ?? root.data ?? data;
  }

  const packages = Array.isArray(data) ? data : [];
  if (!rawItems.length) rawItems = rowsNestedOnPackages(packages);

  return { id: matrixId, items: uniqueScopeItems(rawItems, projectId) };
}

/** Read the project's scope items from GET /report-engine/logs/5. */
export async function loadProjectDoc(
  projectId: string
): Promise<ProjectDoc | null> {
  const projectToken = getDsmProjectToken();
  if (!projectToken) throw new Error("DSM: no project token available yet");

  const res = await getFeatureEntityLog({
    projectToken,
    featureType: DESIGN_PACKAGE_LOG_FEATURE_TYPE
  });
  if (!res.success) {
    throw new Error(res.errorMsg || "DSM: failed to load design-package log");
  }
  return projectDocFromDesignPackageLog(res.data, projectId);
}

/** Create (POST) or update (PUT) the project's scope-matrix record. */
export async function saveProjectDoc(
  items: DsmScopeItem[],
  docId: string | null
): Promise<string> {
  const body = JSON.stringify({
    data: { schemaVersion: DSM_SCHEMA_VERSION, source: "project", items }
  });
  const res = await fetch(docId ? `${DSM_DOC_URL}/${docId}` : DSM_DOC_URL, {
    method: docId ? "PUT" : "POST",
    headers: authHeaders(),
    body
  });
  if (!res.ok) {
    throw new Error(`DSM save failed (${res.status})`);
  }
  const json = await res.json();
  return (json.id as string) ?? docId ?? "";
}

/**
 * Delete the whole scope-matrix record. This drops the record itself, not a
 * single item — removing one item is a saveProjectDoc with the item omitted.
 */
export async function deleteProjectDoc(docId: string): Promise<void> {
  const res = await fetch(`${DSM_DOC_URL}/${docId}`, {
    method: "DELETE",
    headers: authHeaders()
  });
  if (!res.ok) {
    throw new Error(`DSM delete failed (${res.status})`);
  }
}
